import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Define types for our custom policy compliance data
export type PolicyAlert = {
  id: string;
  policy_name: string;
  table_name: string;
  issue_type: string;
  description: string;
  severity: 'High' | 'Medium' | 'Low' | string; // Allow other string values
  status: 'New' | 'Reviewing' | 'Resolved' | 'Ignored' | string; // Allow other string values
  created_at: string;
  resolved_at: string | null;
  policy_definition: string;
  requires_action: boolean;
  notes?: string | null;
};

// Define type for the job status
export type PolicyAuditJobStatus = {
  job_name: string;
  schedule: string;
  active: boolean;
  last_run: string | null;
  next_run: string | null;
};

// Define type for policy enforcement status
export type PolicyEnforcementStatus = {
  is_enabled: boolean;
  last_changed: string | null;
  changed_by: string | null;
};

// Define type for system settings
export type SystemSetting = {
  id: string;
  name: string;
  value: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

/**
 * Hook for admin policy compliance operations
 */
export const usePolicyCompliance = () => {
  const [loading, setLoading] = useState(false);
  const [enforcementEnabled, setEnforcementEnabled] = useState<boolean | null>(null);
  const [enforcementStatus, setEnforcementStatus] = useState<PolicyEnforcementStatus | null>(null);

  /**
   * Audits existing policies for compliance with admin check standards
   */
  const auditExistingPolicies = useCallback(async (): Promise<PolicyAlert[] | null> => {
    try {
      setLoading(true);

      // Check if the function exists by trying to call it
      const { data, error } = await supabase.rpc('audit_existing_admin_policies');

      if (error) {
        // If function doesn't exist, return empty array with info message
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          toast.info('Policy audit feature not yet configured');
          return [];
        }
        toast.error('Failed to audit existing policies');
        return [];
      }

      if (data && Array.isArray(data) && data.length > 0) {
        toast.success('Policy audit completed successfully');
        return data as PolicyAlert[];
      } else {
        toast.info('No policy issues found');
        return [];
      }
    } catch (error: unknown) {
      console.error('Error auditing policies:', error);
      toast.error('Failed to audit existing policies');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Gets the status of the scheduled policy audit job
   */
  const getPolicyAuditJobStatus = useCallback(async (): Promise<PolicyAuditJobStatus | null> => {
    try {
      setLoading(true);

      // Since this function doesn't exist in the current database, return a mock status
      // This prevents errors while the feature is being set up
      console.warn('get_policy_audit_job_status function not available, returning mock data');

      // Return a mock job status indicating the feature is not yet configured
      const mockJobStatus: PolicyAuditJobStatus = {
        job_name: 'daily-admin-policy-audit',
        schedule: '0 3 * * *', // 3 AM daily
        active: false, // Not active since function doesn't exist
        last_run: null,
        next_run: null
      };

      return mockJobStatus;
    } catch (error: unknown) {
      console.error('Error fetching job status:', error);
      toast.error('Failed to fetch scheduled job status');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Attempts to automatically fix a non-compliant policy
   */
  const fixPolicy = useCallback(async (policyName: string, tableName: string): Promise<boolean> => {
    try {
      setLoading(true);

      // Try to call the fix function
      const { data, error } = await supabase.rpc('fix_admin_policy', {
        policy_name_param: policyName,
        table_name_param: tableName
      });

      if (error) {
        // If function doesn't exist, show appropriate message
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          console.warn('Policy fix function not available:', error);
          toast.error('Policy fix feature not yet configured');
          return false;
        }
        console.error('Error fixing policy:', error);
        toast.error('Failed to fix policy: ' + (error.message || 'Unknown error'));
        return false;
      }

      if (data) {
        toast.success('Policy fixed successfully');
        return true;
      } else {
        toast.error('Unable to fix policy automatically');
        return false;
      }
    } catch (error: unknown) {
      console.error('Error fixing policy:', error);
      toast.error('Failed to fix policy');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Toggles policy enforcement on/off for admin policies
   */
  const togglePolicyTrigger = useCallback(async (enable: boolean): Promise<boolean> => {
    try {
      setLoading(true);

      // Try to call the toggle function
      const { data, error } = await supabase.rpc('toggle_admin_policy_trigger', {
        enable
      });

      if (error) {
        // If function doesn't exist, show appropriate message
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          console.warn('Policy enforcement toggle function not available:', error);
          toast.error('Policy enforcement feature not yet configured');
          return false;
        }
        throw error;
      }

      setEnforcementEnabled(enable);
      toast.success(data as string || `Policy enforcement ${enable ? 'enabled' : 'disabled'}`);
      return true;
    } catch (error: unknown) {
      console.error('Error toggling policy trigger:', error);
      toast.error('Failed to toggle policy enforcement');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Gets the current status of the policy enforcement trigger
   *
   * Note: Using direct query to the system_settings table
   */
  const getPolicyEnforcementStatus = useCallback(async (): Promise<PolicyEnforcementStatus | null> => {
    try {
      setLoading(true);

      // Try to query the system_settings table
      const { data, error } = await supabase
        .from('system_settings')
        .select('*')
        .eq('name', 'admin_policy_enforcement')
        .single();

      if (error) {
        // If table doesn't exist or record not found, return default status
        if (error.code === 'PGRST116' || error.message?.includes('relation') || error.message?.includes('does not exist')) {
          console.warn('system_settings table not available or record not found:', error);
          const defaultStatus: PolicyEnforcementStatus = {
            is_enabled: false,
            last_changed: null,
            changed_by: null
          };

          setEnforcementEnabled(false);
          setEnforcementStatus(defaultStatus);
          return defaultStatus;
        } else {
          throw error;
        }
      }

      // Convert system setting to enforcement status
      const setting = data as SystemSetting;
      const status: PolicyEnforcementStatus = {
        is_enabled: Boolean(setting.value?.is_enabled) || false,
        last_changed: setting.updated_at || null,
        changed_by: (setting.value?.changed_by as string) || null
      };

      setEnforcementEnabled(status.is_enabled);
      setEnforcementStatus(status);
      return status;
    } catch (error: unknown) {
      console.error('Error getting policy enforcement status:', error);

      // Return default status instead of showing error to user
      const defaultStatus: PolicyEnforcementStatus = {
        is_enabled: false,
        last_changed: null,
        changed_by: null
      };

      setEnforcementEnabled(false);
      setEnforcementStatus(defaultStatus);
      return defaultStatus;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Updates the status of a policy alert
   */
  const updateAlertStatus = useCallback(async (
    id: string,
    status: 'Reviewing' | 'Resolved' | 'Ignored',
    notes?: string
  ): Promise<boolean> => {
    try {
      setLoading(true);

      // Try to update the alert status
      const { error } = await supabase
        .from('rls_policy_alerts')
        .update({
          status,
          resolved_at: status === 'Resolved' ? new Date().toISOString() : null,
          notes: notes || (status === 'Ignored' ? 'Manually ignored by admin' : null)
        })
        .eq('id', id);

      if (error) {
        // If table doesn't exist, show appropriate message
        if (error.message?.includes('relation') && error.message?.includes('does not exist')) {
          console.warn('rls_policy_alerts table not available:', error);
          toast.error('Policy alerts feature not yet configured');
          return false;
        }
        throw error;
      }

      toast.success(`Alert marked as ${status}`);
      return true;
    } catch (error: unknown) {
      console.error('Error updating alert status:', error);
      toast.error('Failed to update alert status');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    enforcementEnabled,
    enforcementStatus,
    auditExistingPolicies,
    getPolicyAuditJobStatus,
    getPolicyEnforcementStatus,
    fixPolicy,
    togglePolicyTrigger,
    updateAlertStatus
  };
};
